// ============================================================================
// SUPRIE COLOR PALETTE CONFIGURATION
// ============================================================================
// This file contains all the color definitions for the Suprie app.
// Modify these values to customize your app's appearance.
// ============================================================================

export interface ColorPalette {
  primary: ColorShades;
  secondary: ColorShades;
  success: ColorShades;
  petAccents: PetAccentColors;
  health: HealthStatusColors;
}

export interface ColorShades {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
}

export interface PetAccentColors {
  warm: string;
  playful: string;
  gentle: string;
  energetic: string;
  calm: string;
  mint: string;
}

export interface HealthStatusColors {
  excellent: string;
  good: string;
  fair: string;
  poor: string;
  critical: string;
}

// ============================================================================
// CUSTOMIZE YOUR COLORS BELOW
// ============================================================================

// Option 1: Keep the current pet-friendly palette
export const petFriendlyPalette: ColorPalette = {
  primary: {
    50: '#E8F5FF',
    100: '#CCE9FF',
    200: '#99D3FF',
    300: '#66BDFF',
    400: '#33A7FF',
    500: '#0091FF', // Vibrant, trustworthy blue
    600: '#0074CC',
    700: '#005799',
    800: '#003A66',
    900: '#001D33',
  },
  secondary: {
    50: '#FFF4E6',
    100: '#FFE9CC',
    200: '#FFD399',
    300: '#FFBD66',
    400: '#FFA733',
    500: '#FF9100', // Warm, friendly orange
    600: '#E67300',
    700: '#CC5500',
    800: '#B33700',
    900: '#991900',
  },
  success: {
    50: '#E8F8F5',
    100: '#D1F2EB',
    200: '#A3E5D7',
    300: '#76D7C4',
    400: '#48CAB0',
    500: '#1ABC9C', // Healthy green
    600: '#148B6B',
    700: '#0F5A3A',
    800: '#0A2E1F',
    900: '#051F15',
  },
  petAccents: {
    warm: '#FF6B6B',    // Puppy red
    playful: '#4ECDC4', // Kitten teal
    gentle: '#95E1D3',  // Soft mint
    energetic: '#F38BA8', // Pink energy
    calm: '#A8DADC',    // Serene blue-gray
    mint: '#B8E6E1',    // Fresh mint
  },
  health: {
    excellent: '#10B981',
    good: '#34D399',
    fair: '#FBBF24',
    poor: '#F87171',
    critical: '#EF4444',
  }
};

// Option 2: Nature-inspired palette
export const naturePalette: ColorPalette = {
  primary: {
    50: '#F0F9FF',
    100: '#E0F2FE',
    200: '#BAE6FD',
    300: '#7DD3FC',
    400: '#38BDF8',
    500: '#0EA5E9', // Sky blue
    600: '#0284C7',
    700: '#0369A1',
    800: '#075985',
    900: '#0C4A6E',
  },
  secondary: {
    50: '#FEFCE8',
    100: '#FEF9C3',
    200: '#FEF08A',
    300: '#FDE047',
    400: '#FACC15',
    500: '#EAB308', // Golden yellow
    600: '#CA8A04',
    700: '#A16207',
    800: '#854D0E',
    900: '#713F12',
  },
  success: {
    50: '#F0FDF4',
    100: '#DCFCE7',
    200: '#BBF7D0',
    300: '#86EFAC',
    400: '#4ADE80',
    500: '#22C55E', // Forest green
    600: '#16A34A',
    700: '#15803D',
    800: '#166534',
    900: '#14532D',
  },
  petAccents: {
    warm: '#FB7185',    // Rose
    playful: '#06B6D4', // Cyan
    gentle: '#A78BFA',  // Purple
    energetic: '#F59E0B', // Amber
    calm: '#10B981',    // Emerald
    mint: '#B8E6E1',    // Fresh mint
  },
  health: {
    excellent: '#059669',
    good: '#10B981',
    fair: '#F59E0B',
    poor: '#EF4444',
    critical: '#DC2626',
  }
};

// Option 3: Modern minimalist palette
export const modernPalette: ColorPalette = {
  primary: {
    50: '#F8FAFC',
    100: '#F1F5F9',
    200: '#E2E8F0',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B', // Slate gray
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
  },
  secondary: {
    50: '#FEF2F2',
    100: '#FEE2E2',
    200: '#FECACA',
    300: '#FCA5A5',
    400: '#F87171',
    500: '#EF4444', // Red
    600: '#DC2626',
    700: '#B91C1C',
    800: '#991B1B',
    900: '#7F1D1D',
  },
  success: {
    50: '#F0FDF4',
    100: '#DCFCE7',
    200: '#BBF7D0',
    300: '#86EFAC',
    400: '#4ADE80',
    500: '#22C55E', // Green
    600: '#16A34A',
    700: '#15803D',
    800: '#166534',
    900: '#14532D',
  },
  petAccents: {
    warm: '#F97316',    // Orange
    playful: '#8B5CF6', // Violet
    gentle: '#06B6D4',  // Cyan
    energetic: '#EC4899', // Pink
    calm: '#3B82F6',    // Blue
    mint: '#B8E6E1',    // Fresh mint
  },
  health: {
    excellent: '#059669',
    good: '#10B981',
    fair: '#F59E0B',
    poor: '#EF4444',
    critical: '#DC2626',
  }
};

// Modern Healthcare Color System - Refined & Cohesive
export const modernHealthcarePalette: ColorPalette = {
  primary: {
    50: '#F0F9FF',   // Very light blue
    100: '#E0F2FE',  // Light blue
    200: '#BAE6FD',  // Soft blue
    300: '#7DD3FC',  // Medium blue
    400: '#38BDF8',  // Bright blue
    500: '#0EA5E9',  // Main primary - trustworthy blue
    600: '#0284C7',  // Deep blue
    700: '#0369A1',  // Darker blue
    800: '#075985',  // Very dark blue
    900: '#0C4A6E',  // Darkest blue
  },
  secondary: {
    50: '#F8FAFC',   // Very light gray
    100: '#F1F5F9',  // Light gray
    200: '#E2E8F0',  // Soft gray
    300: '#CBD5E1',  // Medium gray
    400: '#94A3B8',  // Muted gray
    500: '#64748B',  // Main secondary - professional gray
    600: '#475569',  // Dark gray
    700: '#334155',  // Darker gray
    800: '#1E293B',  // Very dark gray
    900: '#0F172A',  // Darkest gray
  },
  success: {
    50: '#F0FDF4',   // Very light green
    100: '#DCFCE7',  // Light green
    200: '#BBF7D0',  // Soft green
    300: '#86EFAC',  // Medium green
    400: '#4ADE80',  // Bright green
    500: '#22C55E',  // Main success - healthy green
    600: '#16A34A',  // Deep green
    700: '#15803D',  // Darker green
    800: '#166534',  // Very dark green
    900: '#14532D',  // Darkest green
  },
  petAccents: {
    warm: '#F97316',    // Orange - vaccinations
    playful: '#8B5CF6', // Purple - medications
    gentle: '#06B6D4',  // Cyan - lab results
    energetic: '#EC4899', // Pink - appointments
    calm: '#3B82F6',    // Blue - clinical notes
    mint: '#10B981',    // Emerald - wellness
  },
  health: {
    excellent: '#10B981', // Emerald
    good: '#34D399',     // Light emerald
    fair: '#F59E0B',     // Amber
    poor: '#F87171',     // Light red
    critical: '#EF4444', // Red
  }
};

// ============================================================================
// SUPRIE FOREST PALETTE — synced to iOS brand (AppColors.swift)
// Brand anchor: #2D6A4F (suprieGreen). Sage accent: #52B788 (suprieGreenBright).
// ============================================================================
export const suprieForestPalette: ColorPalette = {
  primary: {
    50: '#EDF7F1',
    100: '#D1ECDD',
    200: '#A8D8BC',
    300: '#7CC199',
    400: '#52B788',  // iOS suprieGreenBright — sage accent
    500: '#2D6A4F',  // iOS suprieGreen — brand anchor
    600: '#245540',
    700: '#1B4131',
    800: '#132E23',
    900: '#0B1A14',
  },
  secondary: {
    50: '#EEFAF3',
    100: '#D7F2E1',
    200: '#AFE4C4',
    300: '#88D6A6',
    400: '#6EC89A',
    500: '#52B788',  // Sage — secondary/accent
    600: '#42A371',
    700: '#368A5D',
    800: '#2A6E4A',
    900: '#1F5337',
  },
  success: {
    50: '#EDF7F1',
    100: '#D1ECDD',
    200: '#A8D8BC',
    300: '#7CC199',
    400: '#52B788',
    500: '#2D6A4F',  // Brand green doubles as success — matches iOS
    600: '#245540',
    700: '#1B4131',
    800: '#132E23',
    900: '#0B1A14',
  },
  petAccents: {
    warm: '#D97706',      // Amber — vaccinations / warnings
    playful: '#8B5CF6',   // Violet — medications
    gentle: '#06B6D4',    // Cyan — labs
    energetic: '#EC4899', // Pink — appointments
    calm: '#64748B',      // Slate — clinical notes
    mint: '#52B788',      // Sage — wellness
  },
  health: {
    excellent: '#2D6A4F', // Forest (brand)
    good: '#52B788',      // Sage
    fair: '#F59E0B',      // Amber
    poor: '#F87171',      // Light red
    critical: '#EF4444',  // Red
  },
};

// ============================================================================
// SELECT YOUR PREFERRED PALETTE
// ============================================================================
// Change this line to use a different palette:
export const activePalette: ColorPalette = suprieForestPalette;

// Or create your own custom palette:
export const customPalette: ColorPalette = {
  // Add your custom colors here
  ...petFriendlyPalette, // Start with existing palette
  // Then override specific colors:
  primary: {
    ...petFriendlyPalette.primary,
    500: '#YOUR_CUSTOM_COLOR', // Replace with your preferred primary color
  },
  // Add more customizations as needed
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const getActivePalette = (): ColorPalette => {
  // You can add logic here to switch palettes based on user preference
  // or other conditions
  return activePalette;
};

export const getPetColor = (colorType: keyof PetAccentColors): string => {
  return activePalette.petAccents[colorType];
};

export const getHealthStatusColor = (status: keyof HealthStatusColors): string => {
  return activePalette.health[status];
}; 